import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useFormikContext, setNestedObjectValues } from "formik";
import cn from "classnames";

import FormikTextField from "components/InputsField/formikInputs/FormikTextField";
import FormikLogotypeInput from "components/InputsField/formikInputs/FormikLogotypeInput";
import { digitsOnlyInput } from "components/InputsField/inputFormats";
import { RolesDropdownService } from "service/dropdownServices/RolesDropdown";
import SelectOptionBoxes from "components/InputsField/formikInputs/SelectOptionBoxes";
import Button from "components/common/Button";
import { sendAim } from "helpers/statisticAim";

import styles from "./styles.module.scss";
import { ERROR_PLACE } from 'components/InputsField/InputError';

const CompanyForm = ({
    setRoles,
    roles,
    setFieldTouched,
    validateForm,
    setTouched,
    setFormStep,
}) => {
    const { values } = useFormikContext();

    const { t } = useTranslation();
    const lang = useSelector((state) => state.settings.lang);

    const requestedRolesLang = useRef(null);

    // `roles` is deliberately kept out of the deps: setRoles always hands back a new array, so
    // depending on it would re-run this effect endlessly whenever the roles list comes back empty
    useEffect(() => {
        if (roles.length > 0 || requestedRolesLang.current === lang) {
            return;
        }
        requestedRolesLang.current = lang;

        RolesDropdownService.getRoles(lang)
            .then((res) => {
                // A failed request resolves with an Error (see RolesDropdownService), so the
                // payload has to be checked — spreading a non-array here fills the form with junk
                setRoles(Array.isArray(res?.data) ? [...res.data] : []);
            })
            .catch((e) => {
                console.log(e);
            });
    }, [lang, setRoles]);

    const handleCompanyRigistration = async () => {
        const validationErrors = await validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setTouched(setNestedObjectValues(validationErrors, true));
        } else {
            sendAim("reg_step2");
            setFormStep(3);
        }
    };

    return (
        <>
            <FormikTextField
                name="name"
                maxLength="64"
                label={t("registration.name")}
                wrapperClassName={styles["field--with-margin"]}
                required
                errorMode={ERROR_PLACE.static}
            />
            <div className={styles.rolesWrapper}>
                <SelectOptionBoxes
                    options={roles}
                    name="company_roles_ids"
                    onTouch={setFieldTouched}
                />
            </div>
            <FormikTextField
                name="payer_number"
                type="string"
                normalize={digitsOnlyInput}
                label={t("registration.payer_number")}
                wrapperClassName={styles["field--with-margin"]}
                required
                errorMode={ERROR_PLACE.static}
            />
            <FormikTextField
                name="legal_address"
                maxLength="255"
                label={t("registration.legal_address")}
                wrapperClassName={styles["field--with-margin"]}
                errorMode={ERROR_PLACE.static}
            />
            <FormikTextField
                name="post_address"
                maxLength="128"
                label={t("registration.post_address")}
                wrapperClassName={styles["field--with-margin"]}
                errorMode={ERROR_PLACE.static}
            />
            <div className={cn(styles.logo, values.logo && styles["logo--filled"])}>
                <FormikLogotypeInput
                    name="logo"
                    label={t("registration.addLogo")}
                    wrapperClassName={styles.logoWrapper}
                />
            </div>
            <div className={styles.button}>
                <Button type="button" onClick={handleCompanyRigistration}>
                    {t("registration.continue_button")}
                </Button>
            </div>
        </>
    );
};

export default CompanyForm;
